'use client';

import { useMemo } from 'react';
import { getComponent } from '@/lib/componentRegistry';
import { PageComponent, DataConfig, TransformedNewsItem, ComponentNavigation, FilterCondition, FieldMapValue, FieldMapEntry } from '@/lib/types/pageBuilder';
import WidgetRenderer from './WidgetRenderer';
import InterstitialAd from './InterstitialAd';
import { IncludeSectionApiData } from '@/lib/pageUtils';

/**
 * Maximum number of items to render per component when loop_count is not specified.
 * This prevents rendering 100+ items at once which causes massive image loading.
 */
const MAX_INITIAL_ITEMS = 10;

/**
 * Normalizes a URL - handles relative paths, full URLs, and domain matching
 * @param url - The URL to normalize (can be relative or absolute)
 * @returns Normalized URL suitable for Next.js Link component
 */
function normalizeNavigationUrl(url: string): string {
  if (!url) return '';

  // If it's already a relative path, return as-is
  if (url.startsWith('/')) {
    return url;
  }

  // If it's a full URL, check if it's the same domain
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const urlObj = new URL(url);
      const currentDomain = typeof window !== 'undefined' ? window.location.hostname : '';

      // List of known app domains - convert full URL to relative path
      const appDomains = ['dinamani.com', 'www.dinamani.com', 'localhost', '127.0.0.1'];
      const isAppDomain = appDomains.some(domain => urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`));
      if (isAppDomain || urlObj.hostname === currentDomain) {
        // Convert to relative path
        return urlObj.pathname + urlObj.search + urlObj.hash;
      }

      // External URL - return as-is
      return url;
    } catch {
      return url;
    }
  }

  // If it's just a slug without leading slash, add one
  return `/${url}`;
}

/**
 * Builds a navigation URL from template and item data
 * Supports multiple navigation types:
 * - 'dynamic': Gets URL from item field (urlField)
 * - 'template': Builds URL with path params and query params
 * - 'direct': Uses urlTemplate directly
 */
function buildNavigationUrl(navigation: ComponentNavigation, item: TransformedNewsItem): string | null {
  // For dynamic type, get URL from item field
  if (navigation.type === 'dynamic' && navigation.urlField) {
    const fieldValue = (item as unknown as Record<string, unknown>)[navigation.urlField];
    if (fieldValue && typeof fieldValue === 'string') {
      return normalizeNavigationUrl(fieldValue);
    }
    // Fallback to navigateUrl or slug if urlField not found
    if (item.navigateUrl && typeof item.navigateUrl === 'string') {
      return normalizeNavigationUrl(item.navigateUrl);
    }
    if (item.slug && typeof item.slug === 'string') {
      return `/${item.slug}`;
    }
    return null;
  }

  // For template type, build URL with path params and query params
  if (navigation.type === 'template' && navigation.urlTemplate) {
    let url = navigation.urlTemplate;

    // Replace path params like {slug} with actual values from item
    if (navigation.pathParams) {
      for (const [placeholder, fieldName] of Object.entries(navigation.pathParams)) {
        const value = (item as unknown as Record<string, unknown>)[fieldName];
        if (value !== undefined && value !== null) {
          // Replace {placeholder} with the actual value
          url = url.replace(`{${placeholder}}`, String(value));
        }
      }
    }

    // Add query params if provided
    if (navigation.params && navigation.params.length > 0) {
      const searchParams = new URLSearchParams();
      for (const param of navigation.params) {
        // Access item field dynamically
        const value = (item as unknown as Record<string, unknown>)[param.fieldName];
        if (value !== undefined && value !== null) {
          searchParams.set(param.paramName, String(value));
        }
      }
      const queryString = searchParams.toString();
      if (queryString) {
        url = `${url}?${queryString}`;
      }
    }
    return url;
  }

  // For direct type or if urlTemplate is provided, return it
  if (navigation.urlTemplate) {
    return navigation.urlTemplate;
  }
  return null;
}
interface ComponentRendererProps {
  component: PageComponent;
  items: TransformedNewsItem[];
  includeSectionApiData?: IncludeSectionApiData;
  /** Mark first component's image as LCP priority for preloading */
  priority?: boolean;
  /** Enable horizontal scroll for mobile/tablet instead of stacked/grid layout */
  horizontalScroll?: boolean;
}

// Static Tailwind grid column classes
const gridColsMap: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6'
};

/**
 * Evaluates a single filter condition against an item
 */
function evaluateCondition(item: TransformedNewsItem, condition: FilterCondition): boolean {
  const itemValue = item[condition.field];
  const conditionValue = condition.value;

  // Convert to strings for comparison
  const itemStr = String(itemValue ?? '').toLowerCase();
  const conditionStr = String(conditionValue).toLowerCase();
  switch (condition.operator) {
    case 'equals':
      return itemStr === conditionStr;
    case 'not_equals':
      return itemStr !== conditionStr;
    case 'contains':
      return itemStr.includes(conditionStr);
    case 'not_contains':
      return !itemStr.includes(conditionStr);
    case 'starts_with':
      return itemStr.startsWith(conditionStr);
    case 'ends_with':
      return itemStr.endsWith(conditionStr);
    default:
      return true;
  }
}

/**
 * Filters items based on data_config filter conditions
 * If filter_enabled is false or no conditions, returns items unchanged
 * Always excludes collection metadata items (used only for heading/breadcrumbs)
 */
function filterItems(items: TransformedNewsItem[], dataConfig?: DataConfig): TransformedNewsItem[] {
  // First, exclude collection metadata items (they're only for heading/breadcrumbs)
  const contentItems = items.filter(item => !item._isCollectionMeta);

  // If no filter enabled or no conditions, return content items
  if (!dataConfig?.filter_enabled || !dataConfig.filter_conditions?.length) {
    return contentItems;
  }
  const conditions = dataConfig.filter_conditions;
  const logic = dataConfig.filter_logic || 'AND';
  return contentItems.filter(item => {
    if (logic === 'AND') {
      return conditions.every(condition => evaluateCondition(item, condition));
    } else {
      return conditions.some(condition => evaluateCondition(item, condition));
    }
  });
}

/**
 * Resolves a field path from an API response item
 * Handles paths like:
 * - "slug" → item.slug
 * - "hero-image-s3-key" → item["hero-image-s3-key"]
 * - "tags[].name" → item.tags[0].name ([] treated as [0])
 * - "story.headline" → item.story.headline
 * - "tags[0].name" → item.tags[0].name
 *
 * PRIORITY: Checks item._raw first (raw API response), then falls back to transformed item
 * This allows field_map to use original API paths like "hero-image-s3-key" even after transformation
 */
function resolveApiFieldPath(item: TransformedNewsItem, fieldPath: string): unknown {
  if (!fieldPath) return undefined;

  // Normalize: replace empty brackets [] with [0]
  const normalizedPath = fieldPath.replace(/\[\]/g, '[0]');
  // Convert array notation to dot notation: tags[0].name → tags.0.name
  const parts = normalizedPath.replace(/\[(\d+)\]/g, '.$1').split('.');

  // Helper to traverse a path on an object
  const traversePath = (obj: unknown, pathParts: string[]): unknown => {
    let value: unknown = obj;
    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      if (value == null) {
        return undefined;
      }
      value = (value as Record<string, unknown>)[part];
    }
    return value;
  };

  // PRIORITY 1: Check _raw first (original API response)
  if (item._raw) {
    const rawValue = traversePath(item._raw, parts);
    if (rawValue !== undefined && rawValue !== null) {
      return rawValue;
    }
  }

  // PRIORITY 2: Fall back to transformed item
  const itemValue = traversePath(item, parts);
  return itemValue;
}

/**
 * Resolves all field map entries, converting apiField paths to actual values
 * Returns props ready to pass to component:
 * - String mapping: returns resolved value directly
 * - Object mapping: returns full object with 'value' instead of 'apiField'
 */
function resolveFieldMapToProps(item: TransformedNewsItem, fieldMap: Record<string, FieldMapValue>): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  if (item._raw?.['story-template'] === 'live-blog' || item._raw?.['is-live-blog']) {
    console.log('[live-blog story] _raw=', JSON.stringify(item._raw));
  }
  for (const [propName, mapping] of Object.entries(fieldMap)) {
    if (typeof mapping === 'string') {
      // Simple string mapping: "slug": "slug" or "title": "headline"
      // Check _raw first (original API), then transformed item, then use path resolution
      const rawValue = item._raw?.[mapping];
      const itemValue = item[mapping];
      const directValue = rawValue !== undefined ? rawValue : itemValue;
      const resolvedValue = directValue !== undefined ? directValue : resolveApiFieldPath(item, mapping);

      // Only add prop if value exists
      if (resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== '') {
        props[propName] = resolvedValue;
      }
    } else if (mapping && typeof mapping === 'object') {
      // Object mapping: { hidden, apiField, apiFields, customClass }
      // Skip hidden fields entirely
      if (mapping.hidden) {
        continue;
      }
      let resolvedValue: unknown = undefined;

      // Build list of fields to try: apiFields array takes precedence, fallback to single apiField
      const fieldsToTry: string[] = mapping.apiFields?.length ? mapping.apiFields : mapping.apiField ? [mapping.apiField] : [];
      for (const field of fieldsToTry) {
        const rawValue = item._raw?.[field];
        const itemValue = item[field];
        const directValue = rawValue !== undefined ? rawValue : itemValue;
        const candidate = directValue !== undefined ? directValue : resolveApiFieldPath(item, field);
        if (candidate !== undefined && candidate !== null && candidate !== '') {
          resolvedValue = candidate;
          break;
        }
      }

      // FALLBACK: If apiField didn't resolve, try using propName directly from item
      if (resolvedValue === undefined || resolvedValue === null || resolvedValue === '') {
        const rawFallback = item._raw?.[propName];
        const itemFallback = item[propName];
        const fallbackValue = rawFallback !== undefined ? rawFallback : itemFallback;
        if (fallbackValue !== undefined && fallbackValue !== null && fallbackValue !== '') {
          resolvedValue = fallbackValue;
        }
      }

      // Only add prop if value exists
      if (resolvedValue !== undefined && resolvedValue !== null && resolvedValue !== '') {
        // Create the resolved object with 'value' instead of 'apiField'
        const resolvedEntry: FieldMapEntry = {
          hidden: mapping.hidden,
          value: resolvedValue,
          customClass: mapping.customClass
        };
        // Remove undefined properties
        if (resolvedEntry.hidden === undefined) delete resolvedEntry.hidden;
        if (resolvedEntry.customClass === undefined || resolvedEntry.customClass === '') delete resolvedEntry.customClass;
        props[propName] = resolvedEntry;
      }
    }
  }
  return props;
}

/**
 * Helper to check if a value is valid (not undefined, null, or empty string)
 */
function isValidValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

/**
 * Maps transformed news item to component props based on field_map configuration
 *
 * Fully dynamic - no hardcoded field names. Props are determined by field_map:
 * - String mapping: "propName": "apiPath" → propName receives resolved value
 * - Object mapping: "propName": { apiField, hidden, customClass } → propName receives { value, hidden, customClass }
 *
 * If field_map is empty/not provided, falls back to standard field extraction for backward compatibility
 * If no data for a field, that prop is NOT passed to the component
 */
function mapItemToProps(item: TransformedNewsItem, componentType: string, defaultProps?: Record<string, unknown>, customHref?: string | null, fieldMap?: Record<string, FieldMapValue>): Record<string, unknown> {
  // Start with default props from component config
  const finalProps: Record<string, unknown> = {
    ...defaultProps
  };

  // If field_map is provided, resolve and apply all mapped fields dynamically
  if (fieldMap && Object.keys(fieldMap).length > 0) {
    const resolvedProps = resolveFieldMapToProps(item, fieldMap);

    // Merge resolved props into final props (overwrites defaults)
    for (const [propName, propValue] of Object.entries(resolvedProps)) {
      const hadDefault = propName in finalProps;
      const oldValue = finalProps[propName];
      finalProps[propName] = propValue;
      if (hadDefault) {} else {}
    }
  } else {
    // No field_map provided - use backward compatible standard field extraction

    // Only add if value is valid
    if (isValidValue(item.title)) {
      finalProps.title = item.title;
    }
    if (isValidValue(item.timeAgo)) {
      finalProps.timeAgo = item.timeAgo;
    }
    if (isValidValue(item.imageSrc)) {
      finalProps.imageSrc = item.imageSrc;
    }
    if (isValidValue(item.slug)) {
      finalProps.slug = item.slug;
    }
    if (isValidValue(item.category)) {
      finalProps.category = item.category;
    }
    if (isValidValue(item.excerpt)) {
      finalProps.excerpt = item.excerpt;
    }
    if (isValidValue(item.description)) {
      finalProps.description = item.description;
    }
  }

  // If customHref is provided (from navigation config), pass it as href
  if (customHref) {
    finalProps.href = customHref;
  }

  // Always extract sectionUrl from sections if not already set (works with or without field_map)
  if (!finalProps.sectionUrl) {
    // Try item.sections first, then item._raw.sections
    const sections = (item.sections || item._raw?.sections) as Array<{
      slug?: string;
      name?: string;
    }> | undefined;
    if (sections && sections.length > 0 && sections[0]?.slug) {
      finalProps.sectionUrl = `/${sections[0].slug}`;
    }
  }

  // Special handling for Slider component (needs slides array)
  if (componentType === 'Slider' && !fieldMap) {
    const images = item.images && item.images.length ? item.images : [item.imageSrc];
    finalProps.slides = images.map(img => ({
      title: item.title,
      image: img,
      slug: item.slug,
      storyTemplate: item['story-template'] || item.storyTemplate
    }));
  }
  return finalProps;
}
export default function ComponentRenderer({
  component,
  items,
  includeSectionApiData,
  priority = false,
  horizontalScroll = false
}: ComponentRendererProps) {
  const wrapperClassName = 'border border-E8E8E8';
  const wrapperClassNameNoBottom = 'border border-E8E8E8 border-b-0';

  // Check if this is a widget/ad component (no component_type)
  if (!component.component_type) {
    return <WidgetRenderer component={component} includeSectionApiData={includeSectionApiData} />;
  }

  // Get the React component from registry
  const Component = getComponent(component.component_type);
  if (!Component) {
    console.error(`[ComponentRenderer] Component NOT FOUND in registry: "${component.component_type}" (id: ${component.id})`);
    return null;
  }
  const dataConfig: DataConfig | undefined = component.data_config;
  const defaultProps = component.props || {};
  const componentType = component.component_type;

  // If no data_config, render with default props (no data binding)
  if (!dataConfig) {
    return <div className={wrapperClassName}>
        <Component {...defaultProps} />
      </div>;
  }

  // Apply filter conditions if enabled, otherwise use standard items
  const filteredItems = filterItems(items, dataConfig);

  // If no items and component expects data, render nothing (no skeleton)
  if (!filteredItems || filteredItems.length === 0) {
    console.error(`[ComponentRenderer] NO ITEMS for "${componentType}" (id: ${component.id}) after filtering! Original items: ${items.length}, filtered: ${filteredItems?.length || 0}`);
    return null;
  }

  // Get navigation config
  const navigation = component.navigation;

  // Single mode - render first item
  if (dataConfig.mode === 'single') {
    const customHref = navigation ? buildNavigationUrl(navigation, filteredItems[0]) : null;
    const fieldMap = dataConfig.field_map;
    const props = mapItemToProps(filteredItems[0], componentType, defaultProps, customHref, fieldMap);
    // Pass priority prop for LCP optimization
    if (priority) {
      (props as Record<string, unknown>).priority = true;
    }
    return <div className={wrapperClassName}>
        <Component {...props} />
      </div>;
  }

  // Loop mode - render multiple items.
  // An explicit loop_count from the page JSON is a deliberate editorial choice
  // (e.g. a "Special Pages" listing configured for 40 items) and must be honored
  // in full, bounded only by how many items actually came back. Only fall back to
  // MAX_INITIAL_ITEMS — to avoid accidentally rendering 100+ images — when the
  // component doesn't specify a loop_count at all.
  const loopCount = Math.min(dataConfig.loop_count ?? MAX_INITIAL_ITEMS, filteredItems.length);
  const gridColumns = dataConfig.grid_columns;
  const fieldMap_0 = dataConfig.field_map;
  const itemsToRender = filteredItems.slice(0, loopCount);
  const sliderStories = useMemo(() => {
    if (componentType !== 'Slider') return undefined;
    return itemsToRender.map(it => {
      const images = it.images && it.images.length ? it.images : [it.imageSrc];
      return {
        title: it.title,
        slides: images.map(img => ({
          title: it.title,
          image: img,
          slug: it.slug,
          storyTemplate: it['story-template'] || it.storyTemplate
        }))
      };
    });
  }, [componentType, itemsToRender]);

  // Interstitial ad config
  const interstitialEnabled = dataConfig.interstitial_enabled;
  // Default to 'equal' distribution for better ad placement
  const interstitialDistribution = dataConfig.interstitial_distribution || 'equal';
  const interstitialInterval = dataConfig.interstitial_interval || 3;
  const interstitialAdSlots = dataConfig.interstitial_ad_slots || [];
  // Max number of interstitial ads to show (based on ad_count or slots array length)
  const interstitialAdCount = dataConfig.interstitial_ad_count || interstitialAdSlots.length || 1;
  // Fallback to legacy single code fields if no slots array
  const legacyMobileCode = dataConfig.interstitial_mobile_code;
  const legacyDesktopCode = dataConfig.interstitial_desktop_code;

  /**
   * Calculate ad positions for equal distribution mode
   * Distributes N ads equally among M items
   * Example: 4 items, 1 ad → position [2] (after item 2)
   * Example: 6 items, 2 ads → positions [2, 4] (after items 2 and 4)
   * Example: 9 items, 2 ads → positions [3, 6] (after items 3 and 6)
   */
  const calculateEqualDistributionPositions = (totalItems: number, adCount: number): number[] => {
    if (totalItems <= 1 || adCount <= 0) return [];

    // Limit ad count to reasonable maximum (can't have more ads than item gaps)
    const effectiveAdCount = Math.min(adCount, totalItems - 1);

    // Calculate interval: divide items by (ads + 1) to get equal segments
    const interval = totalItems / (effectiveAdCount + 1);

    // Generate positions (1-indexed, meaning "after item N")
    const positions: number[] = [];
    for (let i = 1; i <= effectiveAdCount; i++) {
      // Round to nearest integer for the position
      const position = Math.round(interval * i);
      // Ensure position is valid (between 1 and totalItems - 1)
      if (position > 0 && position < totalItems) {
        positions.push(position);
      }
    }
    return positions;
  };

  /**
   * Check if ad code is valid (not empty, null, or whitespace only)
   */
  const isValidAdCode = (code: string | undefined | null): boolean => {
    return !!(code && code.trim().length > 0);
  };

  /**
   * Get the ad slot for a given index
   * Returns null if adIndex exceeds available slots or if no valid ad codes exist
   */
  const getAdSlotForIndex = (adIndex: number) => {
    // Don't show more ads than the count allows
    if (adIndex >= interstitialAdCount) {
      return null;
    }
    let mobileCode: string | undefined;
    let desktopCode: string | undefined;
    if (interstitialAdSlots.length > 0) {
      // Use the slot at this index (no cycling - stop when slots run out)
      if (adIndex >= interstitialAdSlots.length) {
        return null;
      }
      const slot = interstitialAdSlots[adIndex];
      mobileCode = slot?.mobile_code;
      desktopCode = slot?.desktop_code;
    } else if (adIndex === 0) {
      // Fallback to legacy single codes (only for first ad)
      mobileCode = legacyMobileCode;
      desktopCode = legacyDesktopCode;
    }

    // Only return if at least one valid ad code exists
    if (!isValidAdCode(mobileCode) && !isValidAdCode(desktopCode)) {
      return null;
    }
    return {
      mobileCode,
      desktopCode
    };
  };

  /**
   * Check if an interstitial ad should be shown after the given item index
   * @param itemIndex - 0-based index of the current item
   * @param totalItems - total number of items being rendered
   * @returns the ad index (0-based) if ad should be shown, -1 otherwise
   */
  const shouldShowInterstitialAfter = (itemIndex: number, totalItems_0: number): number => {
    if (!interstitialEnabled) return -1;

    // Don't show ad after the last item
    if (itemIndex >= totalItems_0 - 1) return -1;
    if (interstitialDistribution === 'equal') {
      // Equal distribution: calculate positions based on total items and ad count
      const positions_0 = calculateEqualDistributionPositions(totalItems_0, interstitialAdCount);
      // positions are 1-indexed (after item N), itemIndex is 0-indexed
      // So check if (itemIndex + 1) is in positions
      const adIndex_0 = positions_0.indexOf(itemIndex + 1);
      return adIndex_0;
    } else {
      // Interval mode: show ad after every N items
      // (index + 1) % interval === 0 means after item 3, 6, 9... for interval=3
      if ((itemIndex + 1) % interstitialInterval === 0) {
        // Calculate which ad index this is
        return Math.floor(itemIndex / interstitialInterval);
      }
      return -1;
    }
  };
  if (gridColumns && gridColumns > 1) {
    const gridClass = gridColsMap[gridColumns] || 'grid-cols-1';
    // When mobile_grid_separate is true, render mobile/tablet at the configured
    // mobile_grid_columns instead of the default 1-col-mobile / 2-col-tablet step.
    const mobileGridSeparate = dataConfig.mobile_grid_separate === true;
    const mobileGridColumns = dataConfig.mobile_grid_columns;
    const mobileGridClass = mobileGridSeparate && mobileGridColumns ? gridColsMap[mobileGridColumns] || 'grid-cols-1' : 'grid-cols-1';
    const tabletGridClass = mobileGridSeparate ? '' : 'md:grid-cols-2';
    const totalItems_1 = itemsToRender.length;

    // Build elements array - separate items and interstitial ads for horizontal scroll
    const itemElements: React.ReactNode[] = [];
    const interstitialElements: {
      afterIndex: number;
      element: React.ReactNode;
    }[] = [];
    itemsToRender.forEach((item, index) => {
      const customHref_0 = navigation ? buildNavigationUrl(navigation, item) : null;
      const props_0 = mapItemToProps(item, componentType, defaultProps, customHref_0, fieldMap_0);
      if (componentType === 'Slider') {
        (props_0 as any).stories = sliderStories;
        (props_0 as any).initialStoryIndex = index;
      }
      // Pass priority to ALL items in priority row (LCP can be any visible image)
      if (priority) {
        (props_0 as Record<string, unknown>).priority = true;
      }
      // For horizontal grid layouts, keep all borders (don't remove bottom border)
      // Border removal only applies to vertical loops (single column)
      itemElements.push(<div key={`${component.id}-${item.id}-${index}`} className={wrapperClassName}>
          <Component {...props_0} />
        </div>);

      // Check if interstitial ad should be shown after this item (skip for horizontal scroll)
      if (!horizontalScroll) {
        const interstitialAdIndex = shouldShowInterstitialAfter(index, totalItems_1);
        if (interstitialAdIndex >= 0) {
          const adSlot = getAdSlotForIndex(interstitialAdIndex);
          // Only render if we have a valid ad slot with actual code
          if (adSlot && (isValidAdCode(adSlot.mobileCode) || isValidAdCode(adSlot.desktopCode))) {
            interstitialElements.push({
              afterIndex: index,
              element: <InterstitialAd key={`${component.id}-interstitial-${interstitialAdIndex}`} mobileCode={adSlot.mobileCode} desktopCode={adSlot.desktopCode} index={interstitialAdIndex} fullWidth={true} />
            });
          }
        }
      }
    });

    // Horizontal scroll mode: single render — flex scroll on mobile, grid on desktop
    if (horizontalScroll) {
      return <div className={`flex overflow-x-auto scrollbar-hide -mx-4 px-4 lg:grid lg:${gridClass} lg:overflow-visible lg:mx-0 lg:px-0`}>
          {itemElements.map((el, index_0) => <div key={index_0} className="flex-shrink-0 grid [&>*]:grid lg:contents" style={{
          width: 'calc(75vw - 2rem)'
        }}>
              {el}
            </div>)}
        </div>;
    }

    // Non-horizontal: Build final elements array with interstitials inserted
    const finalElements: React.ReactNode[] = [];
    itemElements.forEach((el_0, index_1) => {
      finalElements.push(el_0);
      // Check if there's an interstitial after this index
      const interstitial = interstitialElements.find(i_0 => i_0.afterIndex === index_1);
      if (interstitial) {
        // No wrapper div - InterstitialAd handles col-span-full internally
        // This prevents empty divs when ad returns null due to viewport mismatch
        finalElements.push(interstitial.element);
      }
    });

    // A single item in an otherwise multi-column grid (e.g. mobile_grid_columns: 2
    // with only 1 item returned) would sit pinned to the first cell, leaving the
    // rest of the row visibly empty instead of centered — center it explicitly.
    const singleItemClass = itemElements.length === 1 ? 'justify-items-center' : '';
    // Fixed-width cards (e.g. ImageText) sit pinned to the left edge of a full-width
    // grid cell when the mobile grid is a single column — center them on mobile only,
    // leaving the wider multi-column desktop/tablet grid untouched.
    const mobileSingleColClass = mobileGridClass === 'grid-cols-1' ? 'max-md:justify-items-center' : '';
    return <div className={`grid ${mobileGridClass} ${tabletGridClass} lg:${gridClass} ${singleItemClass} ${mobileSingleColClass}`.replace(/\s+/g, ' ').trim()}>
        {finalElements}
      </div>;
  }

  // No grid - render items directly with interstitial ads
  const totalItems_2 = itemsToRender.length;
  const itemElements_0: React.ReactNode[] = [];
  const interstitialElements_0: {
    afterIndex: number;
    element: React.ReactNode;
  }[] = [];
  itemsToRender.forEach((item_0, index_2) => {
    const customHref_1 = navigation ? buildNavigationUrl(navigation, item_0) : null;
    const props_1 = mapItemToProps(item_0, componentType, defaultProps, customHref_1, fieldMap_0);
    if (componentType === 'Slider') {
      (props_1 as any).stories = sliderStories;
      (props_1 as any).initialStoryIndex = index_2;
    }
    // Pass priority to ALL items in priority row (LCP can be any visible image)
    if (priority) {
      (props_1 as Record<string, unknown>).priority = true;
    }
    // Remove bottom border for the last item
    const isLast = index_2 === totalItems_2 - 1;
    itemElements_0.push(<div key={`${component.id}-${item_0.id}-${index_2}`} className={isLast ? wrapperClassNameNoBottom : wrapperClassName}>
        <Component {...props_1} />
      </div>);

    // Check if interstitial ad should be shown after this item (skip for horizontal scroll)
    if (!horizontalScroll) {
      const interstitialAdIndex_0 = shouldShowInterstitialAfter(index_2, totalItems_2);
      if (interstitialAdIndex_0 >= 0) {
        const adSlot_0 = getAdSlotForIndex(interstitialAdIndex_0);
        // Only render if we have a valid ad slot with actual code
        if (adSlot_0 && (isValidAdCode(adSlot_0.mobileCode) || isValidAdCode(adSlot_0.desktopCode))) {
          interstitialElements_0.push({
            afterIndex: index_2,
            element: <InterstitialAd key={`${component.id}-interstitial-${interstitialAdIndex_0}`} mobileCode={adSlot_0.mobileCode} desktopCode={adSlot_0.desktopCode} index={interstitialAdIndex_0} />
          });
        }
      }
    }
  });

  // Build final elements with interstitials inserted
  const elements: React.ReactNode[] = [];
  itemElements_0.forEach((el_1, index_3) => {
    elements.push(el_1);
    const interstitial_0 = interstitialElements_0.find(i_1 => i_1.afterIndex === index_3);
    if (interstitial_0) {
      elements.push(interstitial_0.element);
    }
  });

  // Horizontal scroll mode: single render — flex scroll on mobile, stacked on desktop
  if (horizontalScroll) {
    return <div className="flex overflow-x-auto scrollbar-hide -mx-4 px-4 lg:block lg:overflow-visible lg:mx-0 lg:px-0">
        {itemElements_0.map((el_2, index_4) => <div key={index_4} className="flex-shrink-0 grid [&>*]:grid lg:!w-auto lg:flex-shrink lg:block" style={{
        width: 'calc(75vw - 2rem)'
      }}>
            {el_2}
          </div>)}
      </div>;
  }
  return <>{elements}</>;
}

/**
 * Renders multiple components with proper data distribution
 */
interface MultiComponentRendererProps {
  components: PageComponent[];
  items: TransformedNewsItem[];
  includeSectionApiData?: IncludeSectionApiData;
  /** Mark first component as priority for LCP optimization */
  priority?: boolean;
  /** Enable horizontal scroll for mobile/tablet instead of stacked/grid layout */
  horizontalScroll?: boolean;
}
export function MultiComponentRenderer({
  components,
  items,
  includeSectionApiData,
  priority = false,
  horizontalScroll = false
}: MultiComponentRendererProps) {
  // Filter out collection metadata items before distributing to components
  // Metadata items are only used for page heading/breadcrumbs
  const contentItems = items.filter(item => !item._isCollectionMeta);
  let currentIndex = 0;
  return <>
      {components.map(component => {
      // Pass priority to ALL components in priority row (LCP can be any image)
      const componentPriority = priority;

      // Skip widgets/ads - they don't consume data
      if (!component.component_type) {
        return <ComponentRenderer key={component.id} component={component} items={[]} includeSectionApiData={includeSectionApiData} horizontalScroll={horizontalScroll} />;
      }
      const dataConfig = component.data_config;

      // No data_config - render with default props, don't consume items
      if (!dataConfig) {
        return <ComponentRenderer key={component.id} component={component} items={[]} includeSectionApiData={includeSectionApiData} priority={componentPriority} horizontalScroll={horizontalScroll} />;
      }

      // Single mode - consume 1 item
      if (dataConfig.mode === 'single') {
        const itemSlice = contentItems.slice(currentIndex, currentIndex + 1);
        currentIndex += 1;
        return <ComponentRenderer key={component.id} component={component} items={itemSlice} includeSectionApiData={includeSectionApiData} priority={componentPriority} horizontalScroll={horizontalScroll} />;
      }

      // Loop mode - consume loop_count items
      const loopCount = dataConfig.loop_count || 1;
      const itemSlice_0 = contentItems.slice(currentIndex, currentIndex + loopCount);
      currentIndex += loopCount;
      return <ComponentRenderer key={component.id} component={component} items={itemSlice_0} includeSectionApiData={includeSectionApiData} priority={componentPriority} horizontalScroll={horizontalScroll} />;
    })}
    </>;
}